import { useLocation } from "react-router-dom";
import { useNavigateWithSameKey } from "../../../../../../../../../../../custom-hooks/navigateWithKey";
import { useDispatch, useSelector } from "react-redux";
import { useContext, useEffect, useState } from "react";
import {
  LoadingModalContext,
  MessagePopupContext,
} from "../../../../../../../../../ServicePage";
import LocatorsPage from "../../../../../shared/LocatorsPage";
import { Box, Button } from "@mui/material";
import {
  ArrowBack as BackIcon,
  ArrowForward as NextIcon,
  FileDownloadOutlined as FileDownloadOutlinedIcon,
} from "@mui/icons-material";
import { handleProgressBar } from "../../../../../../../../../../custom-components/ProgressBar/ProgressBarWithAPI";
import { playwrightCSharpReqnrollPageClass } from "../../../../../../../../../../../services/webServices";
import {
  setLocatorsState,
  setPageClassState,
} from "../../../../../../../../../../../store/slices/test-automation/web/flow-stages/playwirghtCSharpReqnrollSlice";
import CommonDownload from "../../../../../../../../../../custom-components/CommonDownload";

const ReqNRollLocators = () => {
  const navigate = useNavigateWithSameKey();
  const location = useLocation();
  const dispatch = useDispatch();

  const { locationKey } = location.state;

  const { locators, automationPath } = useSelector(
    (state) =>
      state["testAutomation"][locationKey]["webAutomation"][
        "playwrightCSharpReqNRoll"
      ] || {}
  );

  const { setMessage, setMessagePopup } = useContext(MessagePopupContext);
  const { setLoadingState } = useContext(LoadingModalContext);

  const [downloadOpen, setDownloadOpen] = useState(false);

  useEffect(() => {
    if (!locationKey) {
      navigate("/landingpage");
      return;
    }
  }, [locationKey]);

  const handleLocatorsUpdate = (updatedLocators) => {
    dispatch(setLocatorsState(locationKey, updatedLocators));
  };

  const handleBackClick = () => {
    navigate(-1);
  };

  const handleContinueClick = async () => {
    try {
      let progressHandler = handleProgressBar({
        setLoadingState,
        totalDurationMs: 18000,
      });
      progressHandler.start("Generating Page Classes...");

      // Your API call or processing logic here
      const response = await playwrightCSharpReqnrollPageClass(locators);
      dispatch(setPageClassState(locationKey, response.data));

      // Navigate to next step
      navigate(`${automationPath}/page-class`);

      progressHandler.stop();
    } catch (error) {
      setMessage(
        error.response.data.error ||
          "An error occurred while generating the feature file."
      );
      setMessagePopup(true);
    } finally {
      setLoadingState({
        open: false,
        message: "",
        progress: null,
      });
    }
  };

  const tableDataConverter = (locators) => {
    // Validate that locators exists and has data
    if (
      !locators ||
      typeof locators !== "object" ||
      Object.keys(locators).length === 0
    ) {
      return { headers: [], values: [] };
    }

    // Check if the first value exists and has the expected structure
    const firstValue = Object.values(locators)[0];
    if (!firstValue || typeof firstValue !== "object") {
      return { headers: [], values: [] };
    }

    const headers = ["Parameters", ...Object.keys(firstValue)];
    const values = [];
    Object.entries(locators).map(([parameter, locatorValues]) => {
      const eachRow = [parameter, ...Object.values(locatorValues)];
      values.push(eachRow);
    });
    return { headers, values };
  };

  return (
    <Box sx={{ height: "100%" }}>
      {/* Pass both locators and update handler */}
      <LocatorsPage
        locators={locators}
        onLocatorsUpdate={handleLocatorsUpdate}
      />

      <Box
        sx={{
          p: 4,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          backgroundColor: "transparent",
          borderRadius: "0 0 8px 8px",
        }}
      >
        {/* Back Button */}
        <Button
          variant="outlined"
          startIcon={<BackIcon />}
          onClick={handleBackClick}
          sx={{
            height: "40px",
            borderColor: "#0094FB",
            color: "#0094FB",
            textTransform: "none",
            fontWeight: 500,
            borderRadius: "8px",
            borderWidth: "2px",
            paddingTop: "8px",
            paddingRight: "16px",
            paddingBottom: "8px",
            paddingLeft: "16px",
            gap: "8px",
            "&:hover": {
              borderColor: "#0094FB",
              backgroundColor: "rgba(0, 148, 251, 0.04)",
            },
          }}
        >
          Back
        </Button>

        <Box className="flex-container-row" sx={{ gap: "8px" }}>
          {/* Download Button */}
          <Button
            variant="outlined"
            startIcon={<FileDownloadOutlinedIcon />}
            onClick={() => setDownloadOpen(true)}
            disabled={!locators || locators.length === 0}
            sx={{
              height: "40px",
              borderColor: "#0094FB",
              color: "#0094FB",
              textTransform: "none",
              fontWeight: 500,
              borderRadius: "8px",
              borderWidth: "2px",
              paddingTop: "8px",
              paddingRight: "16px",
              paddingBottom: "8px",
              paddingLeft: "16px",
              gap: "8px",
              "&:hover": {
                borderColor: "#0094FB",
                backgroundColor: "rgba(0, 148, 251, 0.04)",
              },
            }}
          >
            Download
          </Button>
          {/* Continue Button */}
          <Button
            variant="contained"
            endIcon={<NextIcon />}
            onClick={handleContinueClick}
            disabled={!locators || locators.length === 0}
            sx={{
              height: "40px",
              backgroundColor: "#0094FB",
              color: "white",
              textTransform: "none",
              fontWeight: 500,
              borderRadius: "8px",
              borderWidth: "2px",
              borderColor: "#0094FB",
              paddingTop: "8px",
              paddingRight: "16px",
              paddingBottom: "8px",
              paddingLeft: "16px",
              "&:hover": {
                backgroundColor: "#0078D4",
                borderColor: "#0078D4",
              },
            }}
          >
            Generate Page Class
          </Button>
        </Box>
      </Box>

      <CommonDownload
        open={downloadOpen}
        onClose={() => setDownloadOpen(false)}
        serviceResponse={[
          {
            heading: "Locators",
            type: "table",
            data: { ...tableDataConverter(locators) },
          },
        ]}
        serviceName="Locators"
        wordData={[
          {
            heading: "Locators",
            type: "table",
            data: { ...tableDataConverter(locators) },
          },
        ]}
        documents={["docx"]}
      />
    </Box>
  );
};

export default ReqNRollLocators;
